import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Phone, Sparkles } from "lucide-react";
import { PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "@/components/site/SunDivider";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import { breadcrumbSchema } from "@/lib/seo";

export default function FacialAestheticsPage() {
  const { t } = useT();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const BENEFITS = [
    { t: t("Youthful Appearance"), d: t("Smooth fine lines and wrinkles to restore a more refreshed look.") },
    { t: t("Harmonious Balance"), d: t("Add subtle volume or soften lines to bring symmetry to your features.") },
    { t: t("Boosted Confidence"), d: t("Feel as good about your face as you do about your smile.") },
    { t: t("Quick, Nonsurgical"), d: t("Most treatments are completed quickly with minimal downtime.") },
    { t: t("Versatile Solutions"), d: t("Customized to the forehead, lips, jawline and TMJ relief.") },
  ];

  return (
    <article data-testid="facial-aesthetics-page">
      <Seo
        title="Facial Aesthetics · BOTOX® & JUVÉDERM® in Leesburg, VA"
        description="Expert-administered BOTOX® and JUVÉDERM® by Dr. Steve Yopa at Smiles of Loudoun in Leesburg, VA. Book your consultation."
        path="/facial-aesthetics"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Facial Aesthetics", path: "/facial-aesthetics" },
        ])}
      />
      {/* Hero */}
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-eyebrow text-[var(--text-muted)]">
              {t("Facial Aesthetics · Leesburg, VA")}
            </div>
            <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[56px] md:text-[96px] leading-[0.96] tracking-tight">
              BOTOX® &<br />
              <span className="italic text-[var(--accent)]">JUVÉDERM®</span>.
            </h1>
            <SunDivider className="mt-6 !justify-start" />
            <p className="mt-5 max-w-xl text-[18px] text-[var(--text-muted)] leading-relaxed">
              {t("Subtle, expert-administered facial aesthetics that complement — never compete with — your smile. The same precise hands that craft your dentistry can soften the lines around it.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={PRACTICE.appointmentUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="facial-appointment-btn"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--primary)] text-[var(--bg)] hover:bg-[var(--primary-dark)]"
              >
                {t("Book a Consultation")} <ArrowUpRight className="w-4 h-4" />
              </a>
              <a
                href={PRACTICE.phoneHref}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
              >
                <Phone className="w-4 h-4" /> {PRACTICE.phone}
              </a>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="arch-shape aspect-[3/4] w-full max-w-[440px] mx-auto bg-[var(--surface)]">
              <img
                src={ASSETS.botox}
                alt="Facial aesthetics at Smiles of Loudoun"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* What It Is */}
      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-[var(--surface)]/60 border-y border-[var(--border)]">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="font-mono-eyebrow text-[var(--accent)]">
              What Are Facial Aesthetic Treatments?
            </div>
            <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
              Noninvasive, natural, <span className="italic text-[var(--accent)]">expertly placed</span>.
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-[var(--text-main)]/85">
              Facial aesthetics encompass nonsurgical treatments that enhance
              facial appearance — creating a more youthful, balanced look that
              complements your smile. These treatments typically include
              noninvasive procedures targeting specific areas of the face,
              improving texture, smoothness and balance.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-[var(--text-main)]/85">
              BOTOX® and dermal fillers like JUVÉDERM® work to reduce signs of
              aging (such as wrinkles or volume loss) and enhance features.
              While these treatments are cosmetic in nature, they can also
              provide additional comfort benefits — such as alleviating jaw
              tension in TMJ cases.
            </p>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <div className="bg-[var(--primary)] text-[var(--bg)] rounded-3xl p-8">
              <div className="font-mono-eyebrow text-[var(--accent)] flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Provided by
              </div>
              <h3 className="font-serif-display text-[28px] md:text-[32px] mt-3 leading-tight">
                Dr. Steve Yopa<br />Dr. Salar Ebrahimi
              </h3>
              <p className="text-[var(--bg)]/80 mt-3 text-sm leading-relaxed">
                The same precise hands that craft your smile. Personalized
                plans designed to feel like you — only more rested.
              </p>
              <div className="mt-6 pt-6 border-t border-[var(--bg)]/15 text-sm space-y-1.5 text-[var(--bg)]/80">
                <div>📍 {PRACTICE.address}</div>
                <div>📞 {PRACTICE.phone}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Two treatments */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="text-center mb-12">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            Discover more
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
            Two treatments, <span className="italic text-[var(--accent)]">infinite</span> nuance.
          </h2>
          <SunDivider className="mt-5" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <TreatmentCard
            name="BOTOX®"
            tag="Smooth lines · Soften expressions · Ease TMJ tension"
            body="A precise neuromodulator used to relax targeted muscles that cause expression lines on the forehead, around the eyes and at the jawline. Delivered in tiny, expertly placed doses for natural movement."
            testid="treatment-botox"
            anchorId="botox"
          />
          <TreatmentCard
            name="JUVÉDERM®"
            tag="Restore volume · Define lips · Refine contours"
            body="A family of hyaluronic acid dermal fillers that restore lost volume, refine lips, and contour cheeks and jawline. Results are immediate and naturally hydrated-looking."
            testid="treatment-juvederm"
            anchorId="juvederm"
          />
        </div>
      </section>

      {/* Benefits */}
      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-[var(--surface)]/60 border-y border-[var(--border)]">
        <div className="text-center mb-12">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            Benefits
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
            Subtle today. <span className="italic text-[var(--accent)]">Confident</span> for years.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`benefit-${i}`}
              className="bg-[var(--bg)] border border-[var(--border)] rounded-3xl p-7"
            >
              <Check className="w-6 h-6 text-[var(--accent)]" />
              <h3 className="font-serif-display text-[24px] mt-4 text-[var(--primary)] leading-tight">
                {b.t}
              </h3>
              <p className="mt-2 text-sm text-[var(--text-muted)] leading-relaxed">
                {b.d}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            Schedule your consultation
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[40px] md:text-[56px] leading-tight tracking-tight">
            Tell us your <span className="italic text-[var(--accent)]">goals</span>.
          </h2>
          <p className="text-[var(--text-muted)] mt-4">
            Personalized, professional care that helps you achieve a confident,
            radiant look tailored to your unique features.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          <ContactForm defaultService="BOTOX® / JUVÉDERM®" />
        </div>
      </section>
    </article>
  );
}

function TreatmentCard({ name, tag, body, testid, anchorId }) {
  return (
    <div
      id={anchorId}
      data-testid={testid}
      className="group bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-8 md:p-10 hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] transition-all scroll-mt-24"
    >
      <div className="font-mono-eyebrow text-[var(--accent)]">{tag}</div>
      <h3 className="font-serif-display text-[42px] md:text-[56px] mt-4 leading-none">
        {name}
      </h3>
      <p className="mt-6 opacity-85 leading-relaxed">{body}</p>
    </div>
  );
}
